import { z } from "zod/v4"
import { StampCardId } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { Period, PeriodState } from "./common"
import { DocumentStatus } from "../common"

export const StampCardDocumentMeta = z.object({
  name: z.string().describe("スタンプカードの表示名。ユーザーに表示されるタイトル"),
  managed_image_id: z.string().describe("カードのサムネイル画像ID（ManagedImageId）"),
  priority: z.number().describe("表示優先度。数値が小さいほど上位に表示されます"),
  status: DocumentStatus.describe("公開状態"),
  mission_period: Period.describe("ミッション達成可能期間。この期間内のみスタンプを獲得できます"),
  claim_period: Period.describe("報酬受取猶予期間。報酬はミッション期間中も受け取り可能。期限後の未受取報酬は自動付与されます"),
})

export type StampCardDocumentMeta = z.infer<typeof StampCardDocumentMeta>

export const StampCardDocumentBody = z.intersection(
  StampCardDocumentMeta,
  z.object({
    reference_managed_image_ids: z.array(z.string()).describe("使用している画像のIDリスト（自動計算）"),
    active_period: Period.describe("アクティブ期間（mission_period.start_at から claim_period.end_at まで、システム管理）"),
    active_period_state: PeriodState.describe("アクティブ期間の状態（システム自動計算、daemon が更新）"),
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type StampCardDocumentBody = z.infer<typeof StampCardDocumentBody>

export type StampCardDocument = StampCardDocumentBody & {
  id: StampCardId,
}

const StampCardCommonRequest = z.object({
  name: z.string().max(100).describe("スタンプカード名"),
  managedImageId: z.string().describe("スタンプカード画像のID（ManagedImageId）"),
  priority: z.int().min(0).describe("表示優先度（数値が大きいほど先に表示）"),
  status: DocumentStatus.describe("公開状態"),
  missionPeriod: z.object({
    startAt: z.number().describe("開始日時 (UNIX Timestamp mills)"),
    endAt: z.number().describe("終了日時 (UNIX Timestamp mills)"),
  }).describe("ミッション達成可能期間"),
  claimPeriod: z.object({
    endAt: z.number().describe("終了日時 (UNIX Timestamp mills)"),
  }).describe("報酬受取猶予期間（開始日時はmissionPeriod.endAtと同一）"),
})

export const CreateStampCardRequest = StampCardCommonRequest.check((ctx) => {
  const data = ctx.value
  if (data.missionPeriod.endAt <= data.missionPeriod.startAt) {
    ctx.issues.push({
      code: "custom",
      message: "ミッション終了日時はミッション開始日時より後である必要があります",
      path: ["missionPeriod", "endAt"],
      input: data.missionPeriod.endAt,
    })
  }
  if (data.claimPeriod.endAt <= data.missionPeriod.endAt) {
    ctx.issues.push({
      code: "custom",
      message: "報酬受取終了日時はミッション終了日時より後である必要があります",
      path: ["claimPeriod", "endAt"],
      input: data.claimPeriod.endAt,
    })
  }
})

export type CreateStampCardRequest = z.infer<typeof CreateStampCardRequest>

export const UpdateStampCardRequest = StampCardCommonRequest.partial()

export type UpdateStampCardRequest = z.infer<typeof UpdateStampCardRequest>

export class StampCardDocuments {

  static toDocumentData(doc: StampCardDocumentBody): object {
    return {
      ...doc,
    }
  }

  /**
   * mission_period と claim_period から active_period を導出する
   * active_period.start_at = mission_period.start_at
   * active_period.end_at = claim_period.end_at
   */
  static deriveActivePeriod(
    missionPeriod: Period,
    claimPeriod: Period
  ): Period {
    return {
      start_at: missionPeriod.start_at,
      end_at: claimPeriod.end_at,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): StampCardDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: StampCardId(doc.id),
      name: data["name"],
      managed_image_id: data["managed_image_id"],
      priority: data["priority"],
      status: data["status"],
      mission_period: data["mission_period"],
      claim_period: data["claim_period"],
      reference_managed_image_ids: data["reference_managed_image_ids"] || [],
      active_period: data["active_period"],
      active_period_state: data["active_period_state"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
